import React, { useState, useCallback } from 'react';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { InspectionTranslationKeys } from 'intl/keys';
import { Button, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NumbersIcon from '@mui/icons-material/Numbers';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ListIcon from '@mui/icons-material/List';
import { useIntl } from 'react-intl';
import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { ADD_ANSWER_BUTTON_SX } from './styles';
import { getAnswerTypeTheme } from './answerTypeTheme';
import { AddAnswerMenuProps } from './types';

const answerTypeConfig = [
  {
    type: AnswerType.Compliance,
    labelId: InspectionTranslationKeys.typeCompliance,
    icon: CheckCircleOutlineIcon,
  },
  {
    type: AnswerType.Numeric,
    labelId: InspectionTranslationKeys.typeNumeric,
    icon: NumbersIcon,
  },
  {
    type: AnswerType.Text,
    labelId: InspectionTranslationKeys.typeText,
    icon: TextFieldsIcon,
  },
  {
    type: AnswerType.OptionSelection,
    labelId: InspectionTranslationKeys.typeOptionSelection,
    icon: ListIcon,
  },
];

export function AddAnswerMenu({
  onAddAnswer,
}: AddAnswerMenuProps): React.ReactElement {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelectType = useCallback(
    (type: AnswerType) => {
      onAddAnswer(type);
      handleClose();
    },
    [onAddAnswer, handleClose]
  );

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        startIcon={<AddIcon />}
        onClick={handleClick}
        sx={ADD_ANSWER_BUTTON_SX}
      >
        {formatTranslation(intl, InspectionTranslationKeys.add)}
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {answerTypeConfig.map(({ type, labelId, icon: Icon }) => (
          <MenuItem key={type} onClick={() => handleSelectType(type)}>
            <ListItemIcon sx={{ color: getAnswerTypeTheme(type).accent }}>
              <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{formatTranslation(intl, unsafeTranslationKey(labelId) )}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export const MemoAddAnswerMenu = React.memo(AddAnswerMenu);
